import { useRef, useState, FormEvent, useMemo } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Calendar, MapPin, Star, Globe, ArrowRight, Sparkles, Send, Heart, X, Upload, Video, CheckCircle, Film, RefreshCw, Loader2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import CircularGallery from "@/components/animations/CircularGallery";
import { useIsMobile } from "@/hooks/use-mobile";
import { useEventsData } from "@/hooks/useEventsData";
import { theme } from "@/theme";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/services/supabase";
import { toast } from "sonner";
import EventsMapboxGlobe from "@/components/globe/EventsMapboxGlobe";

const Events = () => {
  const isMobile = useIsMobile();
  const heroRef = useRef<HTMLDivElement>(null);
  
  // Fetch events data for the CircularGallery
  const { events } = useEventsData({
    enableCache: true,
    cacheDuration: 60 * 60 * 1000, // 1 hour
    autoRefresh: true,
    refreshInterval: 300000, // 5 minutes
  });

  // Format date helper
  const formatDateMonthYear = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    } catch {
      return "";
    }
  };

  // Transform events for CircularGallery - take upcoming events with photos
  const circularGalleryItems = useMemo(() => {
    const now = new Date();
    const upcomingEvents = events
      .filter(event => {
        try {
          const eventDate = new Date(event.date_start);
          return eventDate >= now && event.flyer_url; // Only events with photos
        } catch {
          return false;
        }
      })
      .sort((a, b) => {
        try {
          const dateA = new Date(a.date_start);
          const dateB = new Date(b.date_start);
          return dateA.getTime() - dateB.getTime();
        } catch {
          return 0;
        }
      })
      .slice(0, 12) // Limit to first 12 upcoming events
      .map(event => ({
        image: event.flyer_url,
        text: `${event.city} - ${formatDateMonthYear(event.date_start)}`
      }));

    // If no upcoming events, show some recent ones with photos
    if (upcomingEvents.length === 0 && events.length > 0) {
      return events
        .filter(event => event.flyer_url) // Only events with photos
        .slice(-12) // Take last 12 events
        .map(event => ({
          image: event.flyer_url,
          text: `${event.city} - ${formatDateMonthYear(event.date_start)}`
        }));
    }

    return upcomingEvents;
  }, [events]);
  
  // Collaboration request form state
  const [collaborationForm, setCollaborationForm] = useState({
    festivalName: "",
    city: "",
    country: "",
    eventDates: "",
    instagram: "",
    phone: "",
    bookingRequest: "",
    email: "",
    honeypot: "", // Bot protection
  });

  // Training application form state
  const [isTrainingModalOpen, setIsTrainingModalOpen] = useState(false);
  const [trainingForm, setTrainingForm] = useState({
    fullName: "",
    artistCoupleName: "",
    city: "",
    country: "",
    instagram: "",
    phone: "",
    email: "",
    danceExperience: "",
    dancingVideo: null as File | null,
    honeypot: "", // Bot protection
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTrainingSubmitting, setIsTrainingSubmitting] = useState(false);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  
  // Pre-submit upload states for training video
  const [trainingUploadedUrl, setTrainingUploadedUrl] = useState<string | null>(null);
  const [trainingUploadStatus, setTrainingUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [trainingUploadProgress, setTrainingUploadProgress] = useState(0);

  const handleCollaborationChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setCollaborationForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleTrainingChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {                                                              
    setTrainingForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Robust file type validation (matching TeamApplication)
      const validVideoMimeTypes = [
        'video/mp4', 'video/webm', 'video/ogg', 'video/quicktime',
        'video/x-msvideo', 'video/x-ms-wmv', 'video/3gpp', 'video/x-m4v',
        'video/mpeg', 'video/x-matroska'
      ];
      
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      const validExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'wmv', '3gp', 'm4v', 'mpeg', 'mkv'];
      
      const isValidMimeType = validVideoMimeTypes.includes(file.type) || file.type.startsWith('video/');
      const isValidExtension = fileExtension ? validExtensions.includes(fileExtension) : false;
      
      if (!isValidMimeType && !isValidExtension) {
        toast.error('Please upload a valid video file (MP4, MOV, WebM, etc.)');
        return;
      }
      
      // Validate file size (max 200MB - matching TeamApplication)
      const maxSize = 200 * 1024 * 1024; // 200MB in bytes
      if (file.size > maxSize) {
        toast.error('Video file size must be less than 200MB');
        return;
      }
      
      setTrainingForm(prev => ({ ...prev, dancingVideo: file }));
      
      // Create preview URL
      const videoUrl = URL.createObjectURL(file);
      setVideoPreview(videoUrl);
      
      // Reset upload states when new file is selected
      setTrainingUploadedUrl(null);
      setTrainingUploadStatus('idle');
      setTrainingUploadProgress(0);
    }
  };

  const removeTrainingVideo = () => {
    setVideoPreview(null);
    setTrainingForm(prev => ({ ...prev, dancingVideo: null }));
    setTrainingUploadedUrl(null);
    setTrainingUploadStatus('idle');
    setTrainingUploadProgress(0);
  };

  const handleTrainingUploadNow = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    if (!trainingForm.dancingVideo) {
      toast.error('Please select a video file first');
      return;
    }

    // Prevent double uploads
    if (trainingUploadStatus === 'uploading') {
      return;
    }

    const file = trainingForm.dancingVideo;
    const contentType = file.type || 'application/octet-stream';
    
    setTrainingUploadStatus('uploading');
    setTrainingUploadProgress(0);

    const maxRetries = 3;
    let attempt = 0;

    while (attempt < maxRetries) {
      attempt++;
      
      try {
        // Get presigned URL from edge function with custom folder path
        const { data: presignedData, error: presignedError } = await supabase.functions.invoke('upload-to-r2', {
          body: {
            fileName: file.name,
            contentType: contentType,
            folderPath: 'exclusive_training_submissions',
          },
        });

        if (presignedError || !presignedData?.presignedUrl) {
          const errorMsg = presignedError?.message || presignedData?.error || 'Failed to get upload URL';
          console.error('Presign error:', { presignedError, presignedData });
          throw new Error(errorMsg);
        }

        // Upload file with progress tracking using XHR
        const uploadSuccess = await new Promise<boolean>((resolve) => {
          const xhr = new XMLHttpRequest();
          
          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              const percentComplete = Math.round((event.loaded / event.total) * 100);
              setTrainingUploadProgress(percentComplete);
            }
          });
          
          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(true);
            } else {
              console.error('Upload failed with status:', xhr.status, xhr.responseText);
              resolve(false);
            }
          });
          
          xhr.addEventListener('error', () => {
            console.error('XHR upload error');
            resolve(false);
          });
          
          xhr.open('PUT', presignedData.presignedUrl);
          xhr.setRequestHeader('Content-Type', contentType);
          xhr.send(file);
        });

        if (uploadSuccess) {
          setTrainingUploadedUrl(presignedData.publicUrl);
          setTrainingUploadStatus('success');
          setTrainingUploadProgress(100);
          toast.success('Video uploaded successfully!');
          return;
        }
        
        throw new Error('Upload failed');
      } catch (error) {
        console.error(`Upload attempt ${attempt} failed:`, error);
        
        if (attempt >= maxRetries) {
          setTrainingUploadStatus('error');
          const errorMessage = error instanceof Error ? error.message : 'Failed to upload video';
          toast.error(errorMessage);
        } else {
          // Wait before retry
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
      }
    }
  };

  const handleTrainingSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Bot protection
    if (trainingForm.honeypot !== "") {
      return;
    }

    // Validate required video upload
    if (!trainingForm.dancingVideo) {
      toast.error('Please upload a dancing video');
      return;
    }

    setIsTrainingSubmitting(true);

    try {
      let videoUrl: string | null = null;
      
      // Use pre-uploaded URL if available, otherwise upload now
      if (trainingUploadStatus === 'success' && trainingUploadedUrl) {
        videoUrl = trainingUploadedUrl;
      } else if (trainingForm.dancingVideo) {
        // Fall back to on-submit upload via R2
        const videoFile = trainingForm.dancingVideo;
        const contentType = videoFile.type || 'application/octet-stream';
        
        // Fallback upload with retry logic (matching TeamApplication)
        const maxRetries = 3;
        let uploadAttempt = 0;
        let uploadSuccess = false;
        
        while (uploadAttempt < maxRetries && !uploadSuccess) {
          uploadAttempt++;
          
          try {
            const { data: presignedData, error: presignedError } = await supabase.functions.invoke('upload-to-r2', {
              body: {
                fileName: videoFile.name,
                contentType: contentType,
                folderPath: 'exclusive_training_submissions',
              },
            });

            if (presignedError || !presignedData?.presignedUrl) {
              const errorMsg = presignedError?.message || presignedData?.error || 'Failed to get upload URL';
              console.error('Presign error:', { presignedError, presignedData });
              throw new Error(errorMsg);
            }

            // Upload to R2 using XHR for progress tracking
            const xhrSuccess = await new Promise<boolean>((resolve) => {
              const xhr = new XMLHttpRequest();
              
              xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                  resolve(true);
                } else {
                  console.error('Upload failed with status:', xhr.status);
                  resolve(false);
                }
              });
              
              xhr.addEventListener('error', () => {
                console.error('XHR upload error');
                resolve(false);
              });
              
              xhr.open('PUT', presignedData.presignedUrl);
              xhr.setRequestHeader('Content-Type', contentType);
              xhr.send(videoFile);
            });

            if (xhrSuccess) {
              videoUrl = presignedData.publicUrl;
              uploadSuccess = true;
            } else {
              throw new Error('Upload failed');
            }
          } catch (retryError) {
            console.error(`Fallback upload attempt ${uploadAttempt} failed:`, retryError);
            if (uploadAttempt < maxRetries) {
              // Exponential backoff
              await new Promise(resolve => setTimeout(resolve, 1000 * uploadAttempt));
            }
          }
        }
        
        if (!uploadSuccess) {
          throw new Error('Failed to upload video after multiple attempts');
        }
      }
      
      // Submit training application via Edge Function
      const { data, error } = await supabase.functions.invoke('submit-training-application', {
        body: {
          fullName: trainingForm.fullName,
          artistCoupleName: trainingForm.artistCoupleName,
          city: trainingForm.city,
          country: trainingForm.country,
          instagram: trainingForm.instagram,
          phone: trainingForm.phone,
          email: trainingForm.email,
          danceExperience: trainingForm.danceExperience,
          dancingVideoUrl: videoUrl,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to send request');
      }

      // Success - show toast notification and close modal
      toast.success("Thank you! Your training application has been sent.");
      
      // Reset form
      setTrainingForm({
        fullName: "",
        artistCoupleName: "",
        city: "",
        country: "",
        instagram: "",
        phone: "",
        email: "",
        danceExperience: "",
        dancingVideo: null,
        honeypot: "",
      });
      
      setVideoPreview(null);
      setTrainingUploadedUrl(null);
      setTrainingUploadStatus('idle');
      setTrainingUploadProgress(0);
      setIsTrainingModalOpen(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to send application. Please try again.');
    } finally {
      setIsTrainingSubmitting(false);
    }
  };

  const handleCollaborationSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Bot protection
    if (collaborationForm.honeypot !== "") {
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit collaboration request via Edge Function
      const { data, error } = await supabase.functions.invoke('submit-collaboration-request', {
        body: {
          festivalName: collaborationForm.festivalName,
          email: collaborationForm.email,
          city: collaborationForm.city,
          country: collaborationForm.country,
          eventDates: collaborationForm.eventDates,
          instagram: collaborationForm.instagram,
          phone: collaborationForm.phone,
          bookingRequest: collaborationForm.bookingRequest,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to send request');
      }

      // Success - show toast notification
      toast.success("Thank you! Your collaboration request has been sent.");
      
      // Reset form
      setCollaborationForm({
        festivalName: "",
        city: "",
        country: "",
        eventDates: "",
        instagram: "",
        phone: "",
        bookingRequest: "",
        email: "",
        honeypot: "",
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to send request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Scroll-based animations for hero
  const { scrollYProgress: heroScrollProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  // Zoom in effect - scales from 1 to 1.5
  const scale = useTransform(heroScrollProgress, [0, 1], isMobile ? [1, 1] : [1, 1.5]);
  
  // Fade out as zooming in
  const opacity = useTransform(heroScrollProgress, [0, 0.8, 1], isMobile ? [1, 1, 1] : [1, 0.3, 0]);
  
  // Blur effect for depth
  const blur = useTransform(heroScrollProgress, [0, 1], isMobile ? [0, 0] : [0, 10]);
  const filter = useTransform(blur, (value) => `blur(${value}px)`);

  // Scroll indicator fade out
  const scrollIndicatorOpacity = useTransform(heroScrollProgress, [0, 0.01], [1, 0]);

  return (
    <main 
      className="relative min-h-screen pt-20 lg:pt-[88px]" 
      style={{ 
        backgroundColor: theme.colors.bg,
        position: 'relative',
      }}
    >
      {/* Hero Section - Single Column Layout */}
      <motion.section
        ref={heroRef}
        className="relative h-[120vh] w-full overflow-hidden"
        style={{
          scale,
          opacity,
          position: 'relative', // Explicit position for framer-motion scroll tracking
        }}
      >
        {/* Dark Overlay */}
        <div 
          className="absolute inset-0 z-[1] pointer-events-none"
          style={{
            background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.3) 50%, rgba(0, 0, 0, 0.5) 100%)',
          }}
        />

        {/* Two Equal Columns Layout with InfiniteScroll on Right */}
        <div className="sticky top-0 h-screen w-full flex items-center relative z-10">
          <div className="w-full h-full flex flex-col lg:flex-row">
            {/* Left Side - Content */}
            <motion.div
              className="relative flex items-center justify-start px-8 md:px-12 lg:px-16 xl:px-24 py-12 w-full lg:w-1/2"
              style={{
                background: 'transparent',
                zIndex: 10,
                pointerEvents: 'auto',
              }}
              onMouseMove={(e) => e.stopPropagation()}
              onMouseLeave={(e) => e.stopPropagation()}
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
            >
              <div className="w-full max-w-xl relative">
                {/* Backdrop for title and CTAs */}
                <div 
                  className="absolute inset-0 -mx-4 -my-4 rounded-xl backdrop-blur-sm"
                  style={{
                    background: 'rgba(0, 0, 0, 0.4)',
                    zIndex: -1,
                  }}
                />

                {/* Main Title */}
                <motion.h1
                  className="relative z-10 mb-6 leading-tight"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.7 }}
                  style={{
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "clamp(32px, 6vw, 72px)",
                    fontWeight: 700,
                    lineHeight: 1.1,
                    letterSpacing: "-0.02em",
                    color: theme.colors.text,
                    textShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
                  }}
                >
                  Experience Dance{" "}
                  <span
                    className="inline-block relative"
                    style={{
                      background: theme.gradients.accent,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    Without Borders
                    {/* Animated Underline */}
                    <motion.div
                      className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                      style={{ backgroundColor: theme.colors.accent }}
                      initial={{ scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={{ duration: 0.8, delay: 1.1 }}
                    />
                  </span>
                </motion.h1>

                {/* Subtitle */}
                <motion.p
                  className="relative z-10 mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.1 }}
                  style={{
                    fontFamily: theme.typography.fontFamily,
                    fontSize: "clamp(13px, 1.5vw, 18px)",
                    color: theme.colors.secondary,
                    letterSpacing: "0.02em",
                    lineHeight: 1.6,
                    textShadow: "0 2px 20px rgba(0, 0, 0, 0.5)",
                  }}
                >
                  Join us on an extraordinary journey across continents. From exclusive training intensives to
                  world-renowned festivals.
                </motion.p>

                {/* Stats */}
                <motion.div
                  className="relative z-10 flex flex-wrap items-center gap-6 mb-8"
                  style={{ color: theme.colors.secondary }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.3, duration: 0.8 }}
                >
                  <div className="flex items-center gap-2">
                    <Globe size={20} style={{ color: theme.colors.accent }} />
                    <span style={{ fontFamily: theme.typography.fontFamily, fontSize: "14px" }}>20+ Countries</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar size={20} style={{ color: theme.colors.accent }} />
                    <span style={{ fontFamily: theme.typography.fontFamily, fontSize: "14px" }}>50+ Events</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Star size={20} style={{ color: theme.colors.accent }} />
                    <span style={{ fontFamily: theme.typography.fontFamily, fontSize: "14px" }}>Premium Experience</span>
                  </div>
                </motion.div>

                                {/* CTAs - Three Elegant Buttons */}
                <motion.div
                  className="relative z-10 flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.5, duration: 0.8 }}
                >
                  {/* CTA 1 - Discover our World Tour */}
                  <motion.div
                    className="w-full sm:flex-1"
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.6, duration: 0.6 }}
                  >
                    <button
                      onClick={() => {
                        document.getElementById("worldwide-events")?.scrollIntoView({ behavior: "smooth" });                                               
                      }}
                      className="relative overflow-hidden w-full inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3.5"                                       
                      style={{
                        fontSize: "15px",
                        fontWeight: 600,
                        letterSpacing: "0.02em",
                        color: "#FFFFFF",
                        background: "rgba(230, 126, 34, 0.15)",
                        backdropFilter: "blur(10px)",
                        border: "1.5px solid rgba(230, 126, 34, 0.4)",
                        borderRadius: "12px",
                        boxShadow: "0 4px 16px rgba(230, 126, 34, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)",                                                
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "rgba(230, 126, 34, 0.25)";
                        e.currentTarget.style.borderColor = "rgba(230, 126, 34, 0.7)";
                        e.currentTarget.style.boxShadow = "0 6px 24px rgba(230, 126, 34, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)";                         
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "rgba(230, 126, 34, 0.15)";
                        e.currentTarget.style.borderColor = "rgba(230, 126, 34, 0.4)";
                        e.currentTarget.style.boxShadow = "0 4px 16px rgba(230, 126, 34, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)";                         
                      }}
                    >
                      <Globe className="w-4 h-4" style={{ position: "relative", zIndex: 1 }} />
                      <span style={{ position: "relative", zIndex: 1 }}>Discover our World Tour</span>                                                              
                    </button>
                  </motion.div>

                  {/* CTA 2 - Exclusive Training */}
                  <motion.div
                    className="w-full sm:flex-1"
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.7, duration: 0.6 }}
                  >
                    <button
                      onClick={() => {
                        document.getElementById("exclusive-trainings")?.scrollIntoView({ behavior: "smooth" });                                               
                      }}
                      className="relative overflow-hidden w-full inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3.5"                                       
                      style={{
                        fontSize: "15px",
                        fontWeight: 600,
                        letterSpacing: "0.02em",
                        color: "#FFFFFF",
                        background: "rgba(230, 126, 34, 0.15)",
                        backdropFilter: "blur(10px)",
                        border: "1.5px solid rgba(230, 126, 34, 0.4)",
                        borderRadius: "12px",
                        boxShadow: "0 4px 16px rgba(230, 126, 34, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)",                                                
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "rgba(230, 126, 34, 0.25)";
                        e.currentTarget.style.borderColor = "rgba(230, 126, 34, 0.7)";
                        e.currentTarget.style.boxShadow = "0 6px 24px rgba(230, 126, 34, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)";                         
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "rgba(230, 126, 34, 0.15)";
                        e.currentTarget.style.borderColor = "rgba(230, 126, 34, 0.4)";
                        e.currentTarget.style.boxShadow = "0 4px 16px rgba(230, 126, 34, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)";                         
                      }}
                    >
                      <Star className="w-4 h-4" style={{ position: "relative", zIndex: 1 }} />
                      <span style={{ position: "relative", zIndex: 1 }}>Exclusive Training</span>                                                              
                    </button>
                  </motion.div>

                  {/* CTA 3 - Primary CTA - Collaborate with us */}
                  <motion.div
                    className="w-full sm:flex-1"
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.8, duration: 0.6 }}
                  >
                    <button
                      onClick={() => {
                        document.getElementById("collaboration-request")?.scrollIntoView({ behavior: "smooth" });                                               
                      }}
                      className="relative overflow-hidden w-full inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-3.5"                                       
                      style={{
                        fontSize: "15px",
                        fontWeight: 600,
                        letterSpacing: "0.02em",
                        color: "#FFFFFF",
                        background: "linear-gradient(135deg, #E67E22 0%, #F39C12 100%)",                                                                        
                        border: "1.5px solid rgba(230, 126, 34, 0.5)",
                        borderRadius: "12px",
                        boxShadow: "0 6px 24px rgba(230, 126, 34, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)",                                                
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "linear-gradient(135deg, #F39C12 0%, #E67E22 100%)";                                                 
                        e.currentTarget.style.boxShadow = "0 8px 32px rgba(230, 126, 34, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.3)";                         
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "linear-gradient(135deg, #E67E22 0%, #F39C12 100%)";                                                 
                        e.currentTarget.style.boxShadow = "0 6px 24px rgba(230, 126, 34, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)";                         
                      }}
                    >
                      <span style={{ position: "relative", zIndex: 1 }}>Collaborate with Us</span>                                                              
                      <ArrowRight className="w-4 h-4" style={{ position: "relative", zIndex: 1 }} />                                                            
                    </button>
                  </motion.div>
                </motion.div>

                {/* Scroll Indicator */}
                <motion.div
                  className="relative z-10 flex flex-col items-center gap-2"
                  style={{ opacity: scrollIndicatorOpacity }}
                >
                  <span
                    className="text-xs uppercase tracking-wider"
                    style={{
                      color: theme.colors.textMuted,
                      fontFamily: theme.typography.fontFamily,
                    }}
                  >
                    Scroll to Explore
                  </span>
                  <ArrowRight
                    size={20}
                    style={{ color: theme.colors.accent }}
                    className="transform rotate-90"
                  />
                </motion.div>
              </div>
            </motion.div>

            {/* Right Side - Circular Gallery Component */}
            <motion.div
              className="relative w-full lg:w-1/2"
              style={{
                background: 'transparent',
                filter,
                zIndex: 10,
                pointerEvents: 'auto',
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
              onMouseMove={(e) => e.stopPropagation()}
              onMouseLeave={(e) => e.stopPropagation()}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.5 }}
            >
              {/* Circular Gallery Component */}
              <div className="relative z-10 w-full h-full px-0 py-0 overflow-hidden flex justify-start">
                {circularGalleryItems.length > 0 ? (
                  <div style={{ height: '600px', position: 'relative', width: '100%' }}>
                    <CircularGallery 
                      items={circularGalleryItems}
                      bend={3} 
                      textColor="#ffffff" 
                      borderRadius={0.05} 
                      scrollEase={0.02}
                    />
                  </div>
                ) : (
                  <div className="text-center w-full h-full flex items-center justify-center">
                    <div className="text-lg" style={{ color: theme.colors.secondary }}>
                      Loading events...
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

            {/* Events Globe Section - Worldwide Events */}
      <section id="worldwide-events" className="relative h-[1300px] max-h-[1300px] w-full overflow-hidden flex flex-col -mt-20 lg:-mt-32">                                                             
        <EventsMapboxGlobe />
      </section>

      {/* Exclusive Trainings Section */}
      <section
        id="exclusive-trainings"
        className="relative py-12 lg:py-16 overflow-hidden"
        style={{
          backgroundColor: theme.colors.bg,
        }}
      >
        {/* Decorative Elements */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            background: theme.gradients.radial,
          }}
        />

        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          {/* Section Header */}
          <motion.div
            className="text-center mb-16 lg:mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 mb-4">
              <Star style={{ color: theme.colors.accent }} size={32} />
              <h2
                className="text-4xl lg:text-6xl font-bold"
                style={{
                  fontFamily: theme.typography.fontFamily,
                  background: theme.gradients.accent,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Exclusive Trainings
              </h2>
            </div>
            <p
              className="text-lg lg:text-xl max-w-3xl mx-auto"
              style={{
                color: theme.colors.secondary,
                fontFamily: theme.typography.fontFamily,
              }}
            >
              Intensive programs designed for serious dancers. Limited capacity, personalized attention.
            </p>
          </motion.div>

          {/* Content Grid */}
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto">
            {/* Left Column - Features */}
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
            >
              <div
                className="p-6 rounded-xl border"
                style={{
                  backgroundColor: theme.colors.bgElevated,
                  borderColor: `${theme.colors.accent}4D`,
                }}
              >
                <h3
                  className="text-2xl font-bold mb-4"
                  style={{
                    fontFamily: theme.typography.fontFamily,
                    color: theme.colors.accent,
                  }}
                >
                  What to Expect
                </h3>
                <ul className="space-y-3" style={{ color: theme.colors.secondary, fontFamily: theme.typography.fontFamily }}>
                  <li className="flex items-start gap-3">
                    <Star size={20} style={{ color: theme.colors.accent }} className="mt-0.5 flex-shrink-0" />
                    <span>Small class sizes for personalized instruction</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Star size={20} style={{ color: theme.colors.accent }} className="mt-0.5 flex-shrink-0" />
                    <span>Advanced technique and choreography</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Star size={20} style={{ color: theme.colors.accent }} className="mt-0.5 flex-shrink-0" />
                    <span>Performance opportunities</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Star size={20} style={{ color: theme.colors.accent }} className="mt-0.5 flex-shrink-0" />
                    <span>Certificate of completion</span>
                  </li>
                </ul>
              </div>

              <div
                className="p-6 rounded-xl border"
                style={{
                  backgroundColor: theme.colors.bgElevated,
                  borderColor: `${theme.colors.accent}4D`,
                }}
              >
                <h3
                  className="text-2xl font-bold mb-4"
                  style={{
                    fontFamily: theme.typography.fontFamily,
                    color: theme.colors.accent,
                  }}
                >
                  Duration & Format
                </h3>
                <p style={{ color: theme.colors.secondary, fontFamily: theme.typography.fontFamily }}>
                  Multi-day intensive programs ranging from weekend workshops to week-long immersions.
                  Sessions typically run 6-8 hours per day with breaks.
                </p>
              </div>
            </motion.div>

            {/* Right Column - Application Process */}
            <motion.div
              className="space-y-6"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
            >
              <div
                className="p-6 rounded-xl border"
                style={{
                  background: theme.gradients.warm,
                  borderColor: `${theme.colors.accent}66`,
                }}
              >
                <h3
                  className="text-2xl font-bold mb-6"
                  style={{
                    fontFamily: theme.typography.fontFamily,
                    color: theme.colors.accent,
                  }}
                >
                  Application Process
                </h3>
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div
                      className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold"
                      style={{
                        background: theme.gradients.accent,
                        color: theme.colors.text,
                      }}
                    >
                      1
                    </div>
                    <div>
                                            <h4 className="font-semibold mb-1" style={{ color: theme.colors.text, fontFamily: theme.typography.fontFamily }}>                         
                        Submit Application
                      </h4>
                      <p className="text-sm" style={{ color: theme.colors.secondary, fontFamily: theme.typography.fontFamily }}>                                
                        Complete the online form with your dance background and experience. Video required.                                                                     
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div
                      className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold"
                      style={{
                        background: theme.gradients.accent,
                        color: theme.colors.text,
                      }}
                    >
                      2
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1" style={{ color: theme.colors.text, fontFamily: theme.typography.fontFamily }}>
                        Review & Selection
                      </h4>
                      <p className="text-sm" style={{ color: theme.colors.secondary, fontFamily: theme.typography.fontFamily }}>
                        Our team reviews applications and selects participants based on dedication and skill level.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div
                      className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold"
                      style={{
                        background: theme.gradients.accent,
                        color: theme.colors.text,
                      }}
                    >
                      3
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1" style={{ color: theme.colors.text, fontFamily: theme.typography.fontFamily }}>
                        Confirmation
                      </h4>
                      <p className="text-sm" style={{ color: theme.colors.secondary, fontFamily: theme.typography.fontFamily }}>
                        Selected participants receive confirmation and payment instructions.
                      </p>
                    </div>
                  </div>
                </div>

                <motion.button
                  onClick={() => setIsTrainingModalOpen(true)}
                  className="mt-6 w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2"
                  style={{
                    background: theme.gradients.accent,
                    color: theme.colors.text,
                    fontFamily: theme.typography.fontFamily,
                    boxShadow: theme.shadows.accent,
                  }}
                  whileHover={{ scale: 1.05, boxShadow: theme.shadows.accentHover }}
                  whileTap={{ scale: 0.95 }}
                >
                  Apply Now
                  <ArrowRight size={18} />
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Festivals & World Tour Section */}
      <section
        id="festivals-world-tour"
        className="relative py-12 lg:py-16 overflow-hidden"
        style={{
          backgroundColor: theme.colors.bgElevated,
        }}
      >
        {/* Background Pattern */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `
              linear-gradient(rgba(230, 126, 34, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(230, 126, 34, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: "100px 100px",
          }}
        />

        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          {/* Section Header */}
          <motion.div
            className="text-center mb-16 lg:mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 mb-4">
              <Sparkles style={{ color: theme.colors.accent }} size={32} />
              <h2
                className="text-4xl lg:text-6xl font-bold"
                style={{
                  fontFamily: theme.typography.fontFamily,
                  background: theme.gradients.accent,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Festivals & World Tour
              </h2>
            </div>
            <p
              className="text-lg lg:text-xl max-w-3xl mx-auto"
              style={{
                color: theme.colors.secondary,
                fontFamily: theme.typography.fontFamily,
              }}
            >
              Join us on stage at prestigious festivals and events across the globe. Experience the magic
              of performance in iconic venues worldwide.
            </p>
          </motion.div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12 max-w-7xl mx-auto">
            {[
              {
                icon: Globe,
                title: "International Venues",
                description: "Perform in renowned theaters and festival stages from Europe to Latin America.",
              },
              {
                icon: Calendar,
                title: "Year-Round Schedule",
                description: "Multiple festivals and events throughout the year across different continents.",
              },
              {
                icon: MapPin,
                title: "Global Reach",
                description: "Connect with diverse audiences and cultures in major dance capitals worldwide.",
              },
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                className="text-center p-8 rounded-xl border"
                style={{
                  backgroundColor: theme.colors.bgElevated,
                  borderColor: `${theme.colors.accent}4D`,
                }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
              >
                <div
                  className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
                  style={{
                    background: theme.gradients.warm,
                    border: `2px solid ${theme.colors.accent}66`,
                  }}
                >
                  <feature.icon style={{ color: theme.colors.accent }} size={32} />
                </div>
                <h3
                  className="text-xl font-bold mb-4"
                  style={{
                    fontFamily: theme.typography.fontFamily,
                    color: theme.colors.accent,
                  }}
                >
                  {feature.title}
                </h3>
                <p
                  className="leading-relaxed"
                  style={{
                    color: theme.colors.secondary,
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Call to Action */}
          <motion.div
            className="mt-16 text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div
              className="inline-block p-8 rounded-2xl border max-w-2xl mx-auto"
              style={{
                background: theme.gradients.warm,
                borderColor: `${theme.colors.accent}66`,
              }}
            >
              <h3
                className="text-2xl lg:text-3xl font-bold mb-4"
                style={{
                  fontFamily: theme.typography.fontFamily,
                  color: theme.colors.accent,
                }}
              >
                Ready to Experience Our World Tour?
              </h3>
              <p
                className="mb-6 text-lg"
                style={{
                  color: theme.colors.secondary,
                  fontFamily: theme.typography.fontFamily,
                }}
              >
                Check the map above to find events near you, or follow us on Instagram for the latest
                announcements and behind-the-scenes content.
              </p>
              <motion.button
                className="py-3 px-8 rounded-lg font-semibold transition-all duration-300 inline-flex items-center gap-2"
                style={{
                  background: theme.gradients.accent,
                  color: theme.colors.text,
                  fontFamily: theme.typography.fontFamily,
                  boxShadow: theme.shadows.accent,
                }}
                whileHover={{ scale: 1.05, boxShadow: theme.shadows.accentHover }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  document.getElementById("festivals-world-tour")?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Explore Events
                <ArrowRight size={18} />
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section - Collaboration Request */}
      <section
        id="collaboration-request"
        className="relative py-12 lg:py-16 overflow-hidden"
        style={{
          backgroundColor: theme.colors.bg,
        }}
      >
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            className="max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
          >
            {/* Header */}
            <div className="text-center mb-12">
              <motion.div
                className="inline-flex items-center justify-center mb-4"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Heart style={{ color: theme.colors.accent }} size={40} />
              </motion.div>
              <motion.h2
                className="text-4xl lg:text-6xl font-bold mb-6"
                style={{
                  fontFamily: theme.typography.fontFamily,
                  background: theme.gradients.accent,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Want to bring us to your event?
              </motion.h2>
              <motion.p
                className="text-lg lg:text-xl max-w-2xl mx-auto"
                style={{
                  color: theme.colors.secondary,
                  fontFamily: theme.typography.fontFamily,
                }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                If you're an organizer or festival director interested in inviting Klau & Ros to your event, contact us below.
              </motion.p>
            </div>

            {/* Form Card */}
            <motion.div
              className="rounded-2xl border p-8 lg:p-12"
              style={{
                backgroundColor: theme.colors.bgElevated,
                borderColor: `${theme.colors.accent}4D`,
                boxShadow: theme.shadows.accent,
              }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <form onSubmit={handleCollaborationSubmit} className="space-y-6">
                {/* Honeypot field - hidden from users */}
                <input
                  type="text"
                  name="honeypot"
                  value={collaborationForm.honeypot}
                  onChange={handleCollaborationChange}
                  style={{ display: "none" }}
                  tabIndex={-1}
                  autoComplete="off"
                />

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Festival Name */}
                  <div>
                    <label
                      htmlFor="festivalName"
                      className="block mb-2 text-sm font-semibold"
                      style={{
                        color: theme.colors.text,
                        fontFamily: theme.typography.fontFamily,
                      }}
                    >
                      Festival Name *
                    </label>
                    <Input
                      id="festivalName"
                      name="festivalName"
                      type="text"
                      required
                      value={collaborationForm.festivalName}
                      onChange={handleCollaborationChange}
                      className="bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-orange-500 focus:ring-orange-500"
                      placeholder="Enter festival name"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block mb-2 text-sm font-semibold"
                      style={{
                        color: theme.colors.text,
                        fontFamily: theme.typography.fontFamily,
                      }}
                    >
                      Email *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={collaborationForm.email}
                      onChange={handleCollaborationChange}
                      className="bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-orange-500 focus:ring-orange-500"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                {/* City and Country */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="city"
                      className="block mb-2 text-sm font-semibold"
                      style={{
                        color: theme.colors.text,
                        fontFamily: theme.typography.fontFamily,
                      }}
                    >
                      City *
                    </label>
                    <Input
                      id="city"
                      name="city"
                      type="text"
                      required
                      value={collaborationForm.city}
                      onChange={handleCollaborationChange}
                      className="bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-orange-500 focus:ring-orange-500"
                      placeholder="Enter city"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="country"
                      className="block mb-2 text-sm font-semibold"
                      style={{
                        color: theme.colors.text,
                        fontFamily: theme.typography.fontFamily,
                      }}
                    >
                      Country *
                    </label>
                    <Input
                      id="country"
                      name="country"
                      type="text"
                      required
                      value={collaborationForm.country}
                      onChange={handleCollaborationChange}
                      className="bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-orange-500 focus:ring-orange-500"
                      placeholder="Enter country"
                    />
                  </div>
                </div>

                {/* Event Dates */}
                <div>
                  <label
                    htmlFor="eventDates"
                    className="block mb-2 text-sm font-semibold"
                    style={{
                      color: theme.colors.text,
                      fontFamily: theme.typography.fontFamily,
                    }}
                  >
                    Event Dates *
                  </label>
                  <Input
                    id="eventDates"
                    name="eventDates"
                    type="text"
                    required
                    value={collaborationForm.eventDates}
                    onChange={handleCollaborationChange}
                    className="bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-orange-500 focus:ring-orange-500"
                    placeholder="e.g., March 15-17, 2025 or TBD"
                  />
                </div>

                {/* Instagram and Phone */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="instagram"
                      className="block mb-2 text-sm font-semibold"
                      style={{
                        color: theme.colors.text,
                        fontFamily: theme.typography.fontFamily,
                      }}
                    >
                      Instagram
                    </label>
                    <Input
                      id="instagram"
                      name="instagram"
                      type="text"
                      value={collaborationForm.instagram}
                      onChange={handleCollaborationChange}
                      className="bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-orange-500 focus:ring-orange-500"
                      placeholder="@festivalname"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="phone"
                      className="block mb-2 text-sm font-semibold"
                      style={{
                        color: theme.colors.text,
                        fontFamily: theme.typography.fontFamily,
                      }}
                    >
                      Phone Number
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={collaborationForm.phone}
                      onChange={handleCollaborationChange}
                      className="bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-orange-500 focus:ring-orange-500"
                      placeholder="+1 234 567 8900"
                    />
                  </div>
                </div>

                {/* Booking Request */}
                <div>
                  <label
                    htmlFor="bookingRequest"
                    className="block mb-2 text-sm font-semibold"
                    style={{
                      color: theme.colors.text,
                      fontFamily: theme.typography.fontFamily,
                    }}
                  >
                    Booking Request *
                  </label>
                  <Textarea
                    id="bookingRequest"
                    name="bookingRequest"
                    required
                    value={collaborationForm.bookingRequest}
                    onChange={handleCollaborationChange}
                    rows={6}
                    className="bg-black/50 border-gray-700 text-white placeholder:text-gray-500 focus:border-orange-500 focus:ring-orange-500 resize-none"
                    placeholder="Please describe your booking request (e.g., 2 workshops, 3 workshops, performance, etc.)"
                  />
                </div>

                {/* Submit Button */}
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={!isMobile && !isSubmitting ? { scale: 1.02, y: -2 } : {}}
                  whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                  className="w-full py-4 px-8 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: theme.gradients.accent,
                    color: theme.colors.text,
                    fontFamily: theme.typography.fontFamily,
                    boxShadow: theme.shadows.accent,
                  }}
                >
                  <Send size={20} />
                  {isSubmitting ? "Submitting..." : "Request Collaboration"}
                </motion.button>

                <p
                  className="text-center text-sm mt-4"
                  style={{
                    color: theme.colors.textMuted,
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  We typically respond within 3-5 business days
                </p>
              </form>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Training Application Modal */}
      {isTrainingModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border"
            style={{
              backgroundColor: theme.colors.bgElevated,
              borderColor: `${theme.colors.accent}4D`,
            }}
          >
            {/* Close Button */}
            <button
              onClick={() => setIsTrainingModalOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors"
              style={{ color: theme.colors.text }}
            >
              <X size={24} />
            </button>

            {/* Modal Content */}
            <div className="p-8">
              <h2
                className="text-3xl font-bold mb-4"
                style={{
                  fontFamily: theme.typography.fontFamily,
                  color: theme.colors.text,
                }}
              >
                Apply for Next Training
              </h2>
              <p
                className="mb-6"
                style={{
                  color: theme.colors.secondary,
                  fontFamily: theme.typography.fontFamily,
                }}
              >
                Complete the form below with your details and dance experience. We'll review your application and get back to you within 3-5 business days.
              </p>

              <form onSubmit={handleTrainingSubmit} className="space-y-4">
                {/* Full Name */}
                <div>
                  <label
                    htmlFor="training-fullName"
                    className="block mb-2 text-sm font-semibold"
                    style={{
                      fontFamily: theme.typography.fontFamily,
                      color: theme.colors.text,
                    }}
                  >
                    Full Name *
                  </label>
                  <Input
                    id="training-fullName"
                    name="fullName"
                    value={trainingForm.fullName}
                    onChange={handleTrainingChange}
                    required
                    className="w-full"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      borderColor: "rgba(255, 255, 255, 0.2)",
                    }}
                  />
                </div>

                {/* Artist/Couple Name */}
                <div>
                  <label
                    htmlFor="training-artistCoupleName"
                    className="block mb-2 text-sm font-semibold"
                    style={{
                      fontFamily: theme.typography.fontFamily,
                      color: theme.colors.text,
                    }}
                  >
                    Artist Name / Couple Name *
                  </label>
                  <Input
                    id="training-artistCoupleName"
                    name="artistCoupleName"
                    value={trainingForm.artistCoupleName}
                    onChange={handleTrainingChange}
                    required
                    className="w-full"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      borderColor: "rgba(255, 255, 255, 0.2)",
                    }}
                  />
                </div>

                {/* City and Country */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="training-city"
                      className="block mb-2 text-sm font-semibold"
                      style={{
                        fontFamily: theme.typography.fontFamily,
                        color: theme.colors.text,
                      }}
                    >
                      City *
                    </label>
                    <Input
                      id="training-city"
                      name="city"
                      value={trainingForm.city}
                      onChange={handleTrainingChange}
                      required
                      className="w-full"
                      style={{
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        borderColor: "rgba(255, 255, 255, 0.2)",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="training-country"
                      className="block mb-2 text-sm font-semibold"
                      style={{
                        fontFamily: theme.typography.fontFamily,
                        color: theme.colors.text,
                      }}
                    >
                      Country *
                    </label>
                    <Input
                      id="training-country"
                      name="country"
                      value={trainingForm.country}
                      onChange={handleTrainingChange}
                      required
                      className="w-full"
                      style={{
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        borderColor: "rgba(255, 255, 255, 0.2)",
                      }}
                    />
                  </div>
                </div>

                {/* Instagram */}
                <div>
                  <label
                    htmlFor="training-instagram"
                    className="block mb-2 text-sm font-semibold"
                    style={{
                      fontFamily: theme.typography.fontFamily,
                      color: theme.colors.text,
                    }}
                  >
                    Instagram
                  </label>
                  <Input
                    id="training-instagram"
                    name="instagram"
                    value={trainingForm.instagram}
                    onChange={handleTrainingChange}
                    className="w-full"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      borderColor: "rgba(255, 255, 255, 0.2)",
                    }}
                  />
                </div>

                {/* Phone */}
                <div>
                  <label
                    htmlFor="training-phone"
                    className="block mb-2 text-sm font-semibold"
                    style={{
                      fontFamily: theme.typography.fontFamily,
                      color: theme.colors.text,
                    }}
                  >
                    Phone Number *
                  </label>
                  <Input
                    id="training-phone"
                    name="phone"
                    value={trainingForm.phone}
                    onChange={handleTrainingChange}
                    required
                    className="w-full"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      borderColor: "rgba(255, 255, 255, 0.2)",
                    }}
                  />
                </div>

                {/* Email */}
                <div>
                  <label
                    htmlFor="training-email"
                    className="block mb-2 text-sm font-semibold"
                    style={{
                      fontFamily: theme.typography.fontFamily,
                      color: theme.colors.text,
                    }}
                  >
                    Email *
                  </label>
                  <Input
                    id="training-email"
                    name="email"
                    type="email"
                    value={trainingForm.email}
                    onChange={handleTrainingChange}
                    required
                    className="w-full"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      borderColor: "rgba(255, 255, 255, 0.2)",
                    }}
                  />
                </div>

                {/* Dance Experience */}
                <div>
                  <label
                    htmlFor="training-danceExperience"
                    className="block mb-2 text-sm font-semibold"
                    style={{
                      fontFamily: theme.typography.fontFamily,
                      color: theme.colors.text,
                    }}
                  >
                    Dance Experience *
                  </label>
                  <Textarea
                    id="training-danceExperience"
                    name="danceExperience"
                    value={trainingForm.danceExperience}
                    onChange={handleTrainingChange}
                    required
                    rows={4}
                    className="w-full"
                    style={{
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                      borderColor: "rgba(255, 255, 255, 0.2)",
                    }}
                  />
                </div>

                {/* Dancing Video Upload */}
                <div>
                  <label
                    htmlFor="training-dancingVideo"
                    className="block mb-2 text-sm font-semibold"
                    style={{
                      fontFamily: theme.typography.fontFamily,
                      color: theme.colors.text,
                    }}
                  >
                    Upload Dancing Video *
                  </label>
                  <div className="space-y-3">
                    {/* Success State - Video Uploaded */}
                    {trainingUploadStatus === 'success' && trainingForm.dancingVideo && (
                      <div 
                        className="relative p-4 rounded-lg border-2"
                        style={{
                          borderColor: "rgba(34, 197, 94, 0.5)",
                          backgroundColor: "rgba(34, 197, 94, 0.1)",
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: "rgba(34, 197, 94, 0.2)" }}
                          >
                            <CheckCircle size={24} className="text-green-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-green-400 text-sm">Video ready!</p>
                            <p className="text-xs truncate" style={{ color: theme.colors.secondary }}>
                              {trainingForm.dancingVideo.name}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={removeTrainingVideo}
                            className="p-2 rounded-full hover:bg-white/10 transition-colors"
                          >
                            <X size={16} style={{ color: theme.colors.secondary }} />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Uploading State */}
                    {trainingUploadStatus === 'uploading' && trainingForm.dancingVideo && (
                      <div 
                        className="relative p-4 rounded-lg border-2"
                        style={{
                          borderColor: "rgba(230, 126, 34, 0.5)",
                          backgroundColor: "rgba(230, 126, 34, 0.1)",
                        }}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <Loader2 size={24} className="animate-spin" style={{ color: theme.colors.accent }} />
                          <div className="flex-1">
                            <p className="font-medium text-sm" style={{ color: theme.colors.text }}>
                              Uploading video...
                            </p>
                            <p className="text-xs" style={{ color: theme.colors.secondary }}>
                              {trainingForm.dancingVideo.name}
                            </p>
                          </div>
                          <span className="font-medium" style={{ color: theme.colors.accent }}>
                            {trainingUploadProgress}%
                          </span>
                        </div>
                        <Progress value={trainingUploadProgress} className="h-2" />
                      </div>
                    )}

                    {/* Error State */}
                    {trainingUploadStatus === 'error' && trainingForm.dancingVideo && (
                      <div 
                        className="relative p-4 rounded-lg border-2"
                        style={{
                          borderColor: "rgba(239, 68, 68, 0.5)",
                          backgroundColor: "rgba(239, 68, 68, 0.1)",
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: "rgba(239, 68, 68, 0.2)" }}
                          >
                            <X size={24} className="text-red-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-red-400 text-sm">Upload failed</p>
                            <p className="text-xs truncate" style={{ color: theme.colors.secondary }}>
                              {trainingForm.dancingVideo.name}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={handleTrainingUploadNow}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                              style={{
                                backgroundColor: "rgba(239, 68, 68, 0.2)",
                                color: "#ef4444",
                              }}
                            >
                              <RefreshCw size={14} />
                              Retry
                            </button>
                            <button
                              type="button"
                              onClick={removeTrainingVideo}
                              className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                            >
                              <X size={16} style={{ color: theme.colors.secondary }} />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Idle State with File Selected */}
                    {trainingUploadStatus === 'idle' && trainingForm.dancingVideo && (
                      <div 
                        className="relative p-4 rounded-lg border-2"
                        style={{
                          borderColor: "rgba(255, 255, 255, 0.2)",
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div 
                            className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: "rgba(230, 126, 34, 0.2)" }}
                          >
                            <Film size={20} style={{ color: theme.colors.accent }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate" style={{ color: theme.colors.text }}>
                              {trainingForm.dancingVideo.name}
                            </p>
                            <p className="text-xs" style={{ color: theme.colors.secondary }}>
                              {(trainingForm.dancingVideo.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={handleTrainingUploadNow}
                              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:scale-105"
                              style={{
                                background: theme.gradients.accent,
                                color: theme.colors.text,
                              }}
                            >
                              <Upload size={14} />
                              Upload Now
                            </button>
                            <button
                              type="button"
                              onClick={removeTrainingVideo}
                              className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                            >
                              <X size={16} style={{ color: theme.colors.secondary }} />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* No File Selected */}
                    {!trainingForm.dancingVideo && (
                      <label
                        htmlFor="training-dancingVideo"
                        className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-white/5 transition-colors"
                        style={{
                          borderColor: "rgba(255, 255, 255, 0.2)",
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                        }}
                      >
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <Video size={32} style={{ color: theme.colors.accent }} className="mb-2" />
                          <p className="mb-2 text-sm" style={{ color: theme.colors.text, fontFamily: theme.typography.fontFamily }}>
                            <span className="font-semibold">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs" style={{ color: theme.colors.secondary, fontFamily: theme.typography.fontFamily }}>
                            MP4, MOV, AVI, WebM (MAX. 100MB) <span style={{ color: theme.colors.accent }}>*Required</span>
                          </p>
                        </div>
                        <input
                          id="training-dancingVideo"
                          name="dancingVideo"
                          type="file"
                          accept="video/*"
                          onChange={handleVideoUpload}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Honeypot field (hidden) */}
                <input
                  type="text"
                  name="honeypot"
                  value={trainingForm.honeypot}
                  onChange={handleTrainingChange}
                  style={{ display: 'none' }}
                  tabIndex={-1}
                  autoComplete="off"
                />

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isTrainingSubmitting}
                  className="w-full py-3 px-6 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: isTrainingSubmitting ? theme.colors.secondary : theme.gradients.accent,
                    color: theme.colors.text,
                    fontFamily: theme.typography.fontFamily,
                    boxShadow: theme.shadows.accent,
                  }}
                >
                  {isTrainingSubmitting ? "Submitting..." : "Submit Application"}
                  <Send size={18} />
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </main>
  );
};

export default Events;
